// src/pages/FundPlanner/components/FundStructurer/useFileManager.js
import { useMemo, useCallback } from "react";
import useFileManager from "components/FileManager/hooks/useFileManager";
import { useProfile } from "context/ProfileContext";
import { supabase } from "services/supabaseClient";
import { getSharingPermissions, normalizeSharing } from "lib/permissions";

/* =============================================================================
   FundPlanner File Manager (Option A: Adapter + Modal View)
   -----------------------------------------------------------------------------
   - Linked funds show: Issuer, Issue Name (from ds_deals)
     - Issuer comes from ds_deals.issuer_name_raw (canonical)
     - Issue name comes from ds_deals.name
   - Unlinked funds show: Fund name
============================================================================= */

/* -----------------------------------------------------------------------------
 * Adapter hook: useFundsFileManager
 * -------------------------------------------------------------------------- */

export function useFundsFileManager({ limit = 100 } = {}) {
  const { profile, selectedOrg } = useProfile();

  const {
    records: rawRecords,
    recents,
    loading,
    error,
    fetchRecords,
    addToRecents,
  } = useFileManager({
    table: "fp_funds",
    org: selectedOrg,
    limit,
    select: `
      id,
      org_id,
      tenant_id,
      name,
      created_by,
      updated_at,
      form,
      draw_schedule,
      solution,
      linked_ds_deal_id,
      fund_type,
      sharing,
      linked_deal:ds_deals!fp_funds_linked_ds_deal_fkey (
        id,
        name,
        issuer_name_raw
      )
    `,
  });

  // Annotate ownership / read-only semantics
  const records = useMemo(() => {
    if (!Array.isArray(rawRecords)) return [];

    return rawRecords.map((r) => {
      const permissions = getSharingPermissions(r, { id: profile?.id });
      return {
        ...r,
        sharing: normalizeSharing(r.sharing),
        ...permissions,
      };
    });
  }, [rawRecords, profile?.id]);

  const openRecord = useCallback(
    (fund) => {
      addToRecents?.(fund);
    },
    [addToRecents]
  );

  const deleteFund = useCallback(
    async (fundId) => {
      if (!fundId) throw new Error("Invalid delete request.");
      if (!profile?.id) throw new Error("User not authenticated.");

      const fund = records.find((r) => r.id === fundId);
      if (!fund) throw new Error("Fund not found.");
      if (!fund.canDelete) throw new Error("You do not have permission to delete this fund.");

      const { error: delErr } = await supabase
        .from("fp_funds")
        .delete()
        .eq("id", fundId);

      if (delErr) throw delErr;

      await fetchRecords?.();
    },
    [records, profile?.id, fetchRecords]
  );

  const hasAccessError = useMemo(() => {
    if (!selectedOrg) return false;
    if (loading || error) return false;
    return Array.isArray(records) && records.length === 0;
  }, [selectedOrg, loading, error, records]);

  const isReadOnlyRecord = useCallback(
    (r) => {
      if (!r) return false;
      if (typeof r.isReadOnly === "boolean") return r.isReadOnly;
      return getSharingPermissions(r, { id: profile?.id }).isReadOnly;
    },
    [profile?.id]
  );

  return {
    selectedOrg,

    records,
    recents,
    loading,
    error,

    refresh: fetchRecords,
    deleteRecord: deleteFund,
    openRecord,

    isReadOnlyRecord,

    hasAccessError,

    // NEW: FileRibbon label for the "+ New" action
    newLabel: "New Fund",
  };
}

/* -----------------------------------------------------------------------------
 * View config: fundsOpenView
 * -------------------------------------------------------------------------- */

function normStr(x) {
  return String(x ?? "").trim();
}

function safeText(x) {
  return typeof x === "string" ? x : "";
}

function safeIso(x) {
  return typeof x === "string" ? x : "";
}

function formatISODate(iso) {
  const s = safeIso(iso);
  return s ? s.slice(0, 10) : "";
}

function isFundLinked(r) {
  return !!r?.linked_ds_deal_id;
}

function getLinkedIssuerName(r) {
  return safeText(r?.linked_deal?.issuer_name_raw);
}

function getLinkedIssueName(r) {
  return safeText(r?.linked_deal?.name);
}

function getLinkedIssueLabel(r) {
  const issuer = normStr(getLinkedIssuerName(r));
  const issue = normStr(getLinkedIssueName(r));
  if (issuer && issue) return `${issuer}, ${issue}`;
  return issue || issuer || "";
}

function getFundName(r) {
  if (!isFundLinked(r)) {
    const name = safeText(r?.name) || safeText(r?.form?.fundName);
    return name || "(Untitled)";
  }

  const linked = getLinkedIssueLabel(r);
  return linked || "Linked Fund";
}

function getFundType(r) {
  return safeText(r?.fund_type) || safeText(r?.form?.fundType);
}

/**
 * Settlement date (display-only)
 * Best-effort: prefer form.settlementDate, fall back to other common keys.
 */
function getSettlementDate(r) {
  const v =
    r?.form?.settlementDate ??
    r?.form?.settlement_date ??
    r?.form?.settleDate ??
    r?.form?.settle_date ??
    null;

  return safeText(v);
}

function getTotalAmount(r) {
  const n = Number(r?.form?.totalAmount);
  return Number.isFinite(n) ? n : null;
}

const MONEY0 = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

// Keep these consistent so FileManagerPanel doesn't apply its default width map.
const COL_SMALL = "w-32"; 
const COL_NAME = "w-[calc(100%-30rem)]"; // remaining width after 3x small cols (Fund Type + Settlement + Amount)

export const fundsOpenView = {
  title: "Open Structured Fund",
  defaultSort: { key: "name", asc: true },

  search: {
    placeholder: "Search funds…",
    predicate: (r, q) => {
      const name = normStr(getFundName(r)).toLowerCase();
      const type = normStr(getFundType(r)).toLowerCase();
      const settlement = normStr(getSettlementDate(r)).toLowerCase();
      const amt = getTotalAmount(r);
      const amtStr = amt != null ? String(Math.round(amt)) : "";

      const linkedIssuer = normStr(getLinkedIssuerName(r)).toLowerCase();
      const linkedIssue = normStr(getLinkedIssueName(r)).toLowerCase();
      const linkedId = normStr(r?.linked_ds_deal_id).toLowerCase();

      return (
        name.includes(q) ||
        type.includes(q) ||
        settlement.includes(q) ||
        amtStr.includes(q) ||
        linkedIssuer.includes(q) ||
        linkedIssue.includes(q) ||
        linkedId.includes(q)
      );
    },
  },

  selectedLabel: (r) => normStr(getFundName(r)) || "(Untitled)",

  columns: [
    {
      key: "name",
      header: "Fund",
      align: "left",
      headerClassName: COL_NAME,
      cellClassName: COL_NAME,
      sortValue: (r) => normStr(getFundName(r)).toLowerCase(),
      showReadOnlyBadge: true,
      showLinkedBadge: true,
      render: (r) => {
        const name = getFundName(r);
        return (
          <div className="truncate" title={name}>
            {name}
          </div>
        );
      },
    },
    {
      key: "fund_type",
      header: "Fund Type",
      align: "left",
      headerClassName: COL_SMALL,
      cellClassName: COL_SMALL,
      sortValue: (r) => normStr(getFundType(r)).toLowerCase(),
      render: (r) => {
        const v = getFundType(r);
        return v ? (
          <div className="truncate" title={v}>
            {v}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      key: "settlement",
      header: "Settlement",
      align: "left",
      headerClassName: COL_SMALL,
      cellClassName: COL_SMALL,
      defaultAsc: false,
      sortValue: (r) => normStr(getSettlementDate(r)),
      render: (r) => {
        const v = getSettlementDate(r);
        const shown = v && v.length >= 10 ? formatISODate(v) : v;
        return shown ? (
          <div className="truncate" title={shown}>
            {shown}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      key: "total_amount",
      header: "Amount",
      align: "right",
      headerClassName: COL_SMALL,
      cellClassName: `${COL_SMALL} tabular-nums`,
      defaultAsc: false,
      sortValue: (r) => getTotalAmount(r) ?? -1,
      render: (r) => {
        const amt = getTotalAmount(r);
        return amt != null ? MONEY0.format(amt) : <span className="text-muted">—</span>;
      },
    },
  ],
};
